export const TASK_FIELD_MAX_HEIGHT_PX = 280;

const SINGLE_LINE_HEIGHT_KEY = "__starmeshSingleLineHeightPx";

const restoreTextareaEditingState = (el, { value, selectionStart, selectionEnd, scrollTop, height }) => {
  el.value = value;
  el.style.height = height;
  try {
    el.setSelectionRange(selectionStart, selectionEnd);
  } catch (_) {
    // Best-effort cursor restore after measurement.
  }
  el.scrollTop = scrollTop;
};

/**
 * Measure one line of text including padding (matches legacy single-line input height).
 *
 * @example
 * // Input: empty textarea element
 * // Output: 30 (px, varies by font metrics)
 */
export const measureSingleLineHeight = (el) => {
  const cached = el[SINGLE_LINE_HEIGHT_KEY];
  if (typeof cached === "number" && cached > 0) {
    return cached;
  }

  const savedValue = el.value;
  const selectionStart = el.selectionStart;
  const selectionEnd = el.selectionEnd;
  const scrollTop = el.scrollTop;
  const savedHeight = el.style.height;

  el.value = "x";
  el.style.height = "0px";
  const height = el.scrollHeight;

  restoreTextareaEditingState(el, {
    value: savedValue,
    selectionStart,
    selectionEnd,
    scrollTop,
    height: savedHeight,
  });

  el[SINGLE_LINE_HEIGHT_KEY] = height;
  return height;
};

/**
 * Grow a textarea upward only when content wraps or includes explicit newlines.
 *
 * @example
 * // Input: el.value = "", el
 * // Output: false — height locked to one line
 *
 * @example
 * // Input: el.value with Shift+Enter newline
 * // Output: true — height grows with content
 */
export const resizeAutoGrowTextarea = (el, value = el?.value ?? "") => {
  if (!el) return false;

  const content = String(value);
  const isEmpty = !content.trim();
  const singleLineHeight = measureSingleLineHeight(el);

  if (isEmpty) {
    el.style.height = `${singleLineHeight}px`;
    el.style.overflowY = "hidden";
    return false;
  }

  el.style.height = `${singleLineHeight}px`;
  el.style.height = "0px";
  const scrollHeight = el.scrollHeight;
  const nextHeight = Math.min(
    Math.max(scrollHeight, singleLineHeight),
    TASK_FIELD_MAX_HEIGHT_PX
  );
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = scrollHeight > TASK_FIELD_MAX_HEIGHT_PX ? "auto" : "hidden";

  return content.includes("\n") || nextHeight > singleLineHeight + 1;
};
